import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import { useAuth } from '../context/AuthContext';
import { startCheckout, billingErrorMessage, isAlreadySubscribed, openBillingPortal } from '../utils/billing';
import usePageMeta from '../hooks/usePageMeta';
import { getPlan, formatDollars } from '../config/plans';
import useLivePlans from '../hooks/useLivePlans';
import { SUPPORT_EMAIL } from '../config/support';


// ---------------------------------------------------------------------------
// MARKETING TRUTH RULE (audit B2 / Finding 2.8a)
//
// Everything listed as "included" below must be implemented and reachable in
// the product today. Anything planned but not built does not appear on this
// page at all - not as an included feature, and not as a promise either.
//
// Deliberately NOT advertised, because none of it exists:
//   - per-storm metering / per-storm charges (no metering exists, and flat
//     monthly pricing with seat and customer caps is now the decided structure,
//     so it is not coming either)
//   - Autopay and the Autopay discount (Stripe Checkout already puts a card on
//     file and no discount attaches to it)
//   - real-time GPS crew tracking
//   - a developer API
//   - per-company custom branding
//
// CUSTOMER TEXTING is built (bulk texts, arrival alerts, Pro and above) but is
// not listed here, and every public mention of it - the landing meta
// description, its JSON-LD and llms.txt - says "customer texting (Pro)", never
// a flat "customer texting". It is Pro-only, and a new contractor cannot text
// until provisioned numbers ship (today it needs their own Twilio account and
// carrier registration), so the copy must not read as a promise every plan can
// use on day one.
//
// COMMERCIAL ACCOUNTS (lots and contracts) are specified for the 2027-28
// season (docs/plans/commercial-pricing-spec.md) and not built. They appear
// nowhere on this page; the landing page carries a waitlist only, and llms.txt
// says they are planned.
//
// There is no roadmap section on this page. Every one of those is either
// contradicted by a decision already made or not started, and a page with no
// promises on it is a page that cannot come true late.
//
// Support levels (email / priority / dedicated manager) and custom
// integrations are service commitments, not code features, and are honest.
//
// TIER GATES, and why several features sit on every plan (audit C5 /
// Finding 2.8d). The customer portal, team accounts and referral discounts
// were advertised as Premium- or Pro-only but carry no requireTier on the
// server and no FeatureGate in the client - a Basic company can already use
// all three. Photo uploads were sold as Pro and above and are in the same
// position: no gate exists anywhere, so Basic companies upload photos today.
// Rather than add gates and take working features off paying customers
// mid-season, this page shows all of them where they actually are: on every
// plan. The gates that do exist and are enforced server-side are
// requireTier('pro') on expenses, reports, Square invoicing and bulk email.
//
// WHAT SEPARATES THE PLANS is now users and customers, enforced server-side by
// server/config/planLimits.js on CREATE only. That resolves the open business
// question this comment used to record: Premium is no longer "Pro plus
// priority support", it is 10 users and 200 customers. The numbers below are a
// DISPLAY MIRROR of that file, which is authoritative.
// ---------------------------------------------------------------------------

const TIER_ORDER = ['basic', 'pro', 'premium', 'enterprise'];

// Prices come from config/plans.js, the one client copy of the price list, so
// these cards, the comparison table, the landing page and the landing page's
// structured data cannot disagree with each other. These are the STATIC
// defaults that pre-render; Pricing() lays the live prices from GET /api/plans
// over every place below that shows one (hooks/useLivePlans.js, livePricing).
const planPrice = (id) => getPlan(id).price;

// The cards, the comparison table and the accordion with each price taken from
// `plans` (the live list) rather than the static defaults above.
export const livePricing = (plans) => {
  const price = (id) => plans.find((plan) => plan.id === id).price;
  return {
    tiers: TIERS.map((tier) => ({ ...tier, price: price(tier.id) })),
    matrix: MATRIX.map((group) => ({
      ...group,
      rows: group.rows.map((row) => (row.label === 'Monthly price'
        ? { ...row, values: TIER_ORDER.map((id) => `$${formatDollars(price(id))}`) }
        : row)),
    })),
    comparison: COMPARISON_DATA.map((plan) => ({ ...plan, price: price(plan.id) })),
  };
};

const TIERS = [
  {
    id: 'basic',
    name: 'Basic',
    price: planPrice('basic'),
    description: 'For the solo operator.',
    limits: '1 user. Up to 25 customers.',
    features: [
      { text: 'Route building and optimization', included: true },
      { text: 'Storm Mode', included: true },
      { text: 'Weather forecasts', included: true },
      { text: 'Public customer intake form', included: true },
      { text: 'Service history', included: true },
      { text: 'Customer portal', included: true },
      { text: 'Referral discounts', included: true },
      { text: 'Photo uploads at a stop', included: true },
      { text: 'Unlimited properties per customer', included: true },
      { text: 'Feature requests', included: true },
      { text: 'Email support', included: true },
      { text: 'Square invoicing', included: false },
      { text: 'Expense tracking and P&L reports', included: false },
      { text: 'Bulk customer email', included: false },
    ],
    cta: 'Start Free Trial',
    ctaType: 'trial',
    highlighted: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: planPrice('pro'),
    description: 'For a small crew that bills its own customers.',
    limits: '3 users. Up to 75 customers.',
    features: [
      { text: 'Everything in Basic, plus:', included: true, isHeader: true },
      { text: 'Invoicing from your own Square account', included: true },
      { text: 'Payment status tracking', included: true },
      { text: 'Expense tracking', included: true },
      { text: 'Profit and loss reports', included: true },
      { text: 'Bulk customer email', included: true },
      { text: 'Priority email support', included: false },
    ],
    cta: 'Start Free Trial',
    ctaType: 'trial',
    highlighted: false,
  },
  {
    id: 'premium',
    name: 'Premium',
    price: planPrice('premium'),
    description: 'For a full route list and a real team.',
    limits: '10 users. Up to 200 customers.',
    badge: 'Most Popular',
    features: [
      { text: 'Everything in Pro, plus:', included: true, isHeader: true },
      { text: 'Priority email support', included: true },
      { text: 'Dedicated account manager', included: false },
      { text: 'Custom integrations', included: false },
    ],
    cta: 'Start Free Trial',
    ctaType: 'trial',
    highlighted: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: planPrice('enterprise'),
    description: 'For multi-crew operations.',
    limits: 'Unlimited users. Unlimited customers.',
    features: [
      { text: 'Everything in Premium, plus:', included: true, isHeader: true },
      { text: 'Dedicated account manager', included: true },
      { text: 'Custom integrations', included: true },
      { text: 'Onboarding assistance', included: true },
    ],
    cta: 'Contact Sales',
    ctaType: 'sales',
    highlighted: false,
  },
];

const FAQS = [
  {
    question: 'How does the free trial work?',
    answer: 'Your trial covers one complete storm event, start to finish. That is enough to load your customers, build your routes and work a real storm before you decide. The trial runs at Pro, so you can try the Pro features too.',
  },
  {
    question: 'Do I need a credit card to start?',
    answer: 'No. Registration takes an email and a password. Nothing is charged automatically and no card is on file during the trial.',
  },
  {
    question: 'How do I start a paid plan?',
    answer: 'Pick a plan and pay by card. Checkout is handled by Stripe, so your card details go to Stripe and never to us. Your plan is active as soon as the payment clears.',
  },
  {
    question: 'Can I change or cancel my plan later?',
    answer: 'Yes, from Settings. The billing portal lets you change plan, update your card, download invoices and cancel. Cancelling keeps your access until the end of the period you already paid for.',
  },
  {
    question: 'How many users and customers do I get?',
    answer: 'Basic is 1 user and 25 customers. Pro is 3 users and 75 customers. Premium is 10 users and 200 customers. Enterprise has no limit on either. Properties under a customer are unlimited on every plan, and so are storm events.',
  },
  {
    question: 'What happens if I hit a limit?',
    answer: 'You keep everything you have. You cannot add another customer or invite another user until you upgrade or free a slot. Routes, Storm Mode, invoicing and reports all keep working. If someone signs up through your public intake form while you are full, we hold their signup for you rather than turning them away, and tell you it is waiting.',
  },
  {
    // The shared pool is stated here rather than buried, because a bundle
    // company that assumed two separate allowances would find out by being
    // refused. See decision D6.
    question: 'I run snow and lawn. Do I get a separate allowance for each?',
    answer: 'No. Snow and lawn customers share one customer allowance on your plan, so a Premium account covers 200 customers in total across both. Users are shared the same way.',
  },
  {
    question: 'How do I invoice my own customers?',
    answer: 'Connect your own Square account in Settings, on Pro and above. Invoices go out from your business, and the money goes to your Square account. We do not take a cut and we never hold your customers’ money.',
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'For your SnowTrack plan: card, through Stripe. What you accept from your own customers is up to you and your Square account.',
  },
  {
    question: 'Is there a contract?',
    answer: 'No. Month to month, cancel from Settings whenever you want.',
  },
  {
    question: 'What happens to my data if I leave?',
    answer: 'It stays yours. Export it from the app, and delete your company and everything in it from Settings whenever you want. The Privacy Policy has the detail.',
  },
];

// Single source of truth for both the desktop table and the mobile accordion.
// Cell values: true = included, false = not included, any string = rendered as
// text. There is no 'soon' value any more, because there is no roadmap on this
// page; see the note at the top of the file.
const MATRIX = [
  {
    category: 'Pricing',
    rows: [
      { label: 'Monthly price', values: TIER_ORDER.map((id) => `$${planPrice(id)}`) },
      { label: 'Per-storm charge', values: ['None', 'None', 'None', 'None'] },
    ],
  },
  {
    // The real ladder. These four numbers are a display mirror of
    // server/config/planLimits.js, which is what actually enforces them.
    category: 'Plan limits',
    rows: [
      { label: 'Users included', values: ['1', '3', '10', 'Unlimited'] },
      { label: 'Customers included', values: ['25', '75', '200', 'Unlimited'] },
      { label: 'Properties per customer', values: ['Unlimited', 'Unlimited', 'Unlimited', 'Unlimited'] },
      { label: 'Storm events', values: ['Unlimited', 'Unlimited', 'Unlimited', 'Unlimited'] },
    ],
  },
  {
    category: 'Core Features',
    rows: [
      { label: 'Customer and property records', values: [true, true, true, true] },
      { label: 'Route building and optimization', values: [true, true, true, true] },
      { label: 'Storm Mode', values: [true, true, true, true] },
      { label: 'Weather forecasts', values: [true, true, true, true] },
      { label: 'Public customer intake form', values: [true, true, true, true] },
      { label: 'Service history', values: [true, true, true, true] },
      { label: 'Photo uploads at a stop', values: [true, true, true, true] },
      { label: 'Feature requests', values: [true, true, true, true] },
    ],
  },
  {
    // Included on every plan. See the C5 note at the top of this file.
    category: 'Teams & Customers',
    rows: [
      { label: 'Customer portal', values: [true, true, true, true] },
      { label: 'Team accounts (multi-user)', values: [true, true, true, true] },
      { label: 'Referral discount system', values: [true, true, true, true] },
    ],
  },
  {
    category: 'Business Tools',
    rows: [
      { label: 'Square invoicing from your own account', values: [false, true, true, true] },
      { label: 'Payment status tracking', values: [false, true, true, true] },
      { label: 'Expense tracking', values: [false, true, true, true] },
      { label: 'Profit and loss reports', values: [false, true, true, true] },
      { label: 'Bulk customer email', values: [false, true, true, true] },
    ],
  },
  {
    category: 'Support',
    rows: [
      { label: 'Email support', values: [true, true, true, true] },
      { label: 'Priority email support', values: [false, false, true, true] },
      { label: 'Dedicated account manager', values: [false, false, false, true] },
      { label: 'Custom integrations', values: [false, false, false, true] },
      { label: 'Onboarding assistance', values: [false, false, false, true] },
    ],
  },
];

// Mobile accordion data, derived from MATRIX so the two views can never drift.
const COMPARISON_DATA = TIERS.map((tier, idx) => ({
  id: tier.id,
  name: tier.name,
  price: tier.price,
  badge: tier.badge,
  features: MATRIX
    .filter((group) => group.category !== 'Pricing')
    .flatMap((group) => group.rows.map((row) => ({
      name: row.label,
      value: row.values[idx],
    }))),
}));

// Renders one comparison cell for any of the three cell value shapes.
// "Not included" is a cross rather than a dash: it reads against the check
// mark, and it keeps the em dash character off a public page.
function MatrixCell({ value }) {
  if (value === true) {
    return <span className="check">&#10003;</span>;
  }
  if (value === false) {
    return <span className="cross">&#10005;</span>;
  }
  return <span>{value}</span>;
}

function Pricing() {
  const navigate = useNavigate();
  const { getAppName } = useConfig();
  // /pricing is a public route, but AuthProvider wraps the whole app in index.js,
  // so useAuth is safe here and user is simply null for logged-out visitors.
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [openFaq, setOpenFaq] = useState(null);
  const [openComparison, setOpenComparison] = useState(null);
  // Which plan's button is mid-request. Held as the plan id rather than a
  // boolean so only the clicked card shows a spinner.
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [checkoutError, setCheckoutError] = useState('');
  // Checkout was refused because the company already pays. The portal is
  // owner only server-side, so only an owner is offered the button.
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);

  // Stripe's cancel_url lands here. Nothing was charged and no subscription
  // was created, so this is information rather than an error.
  const checkoutCancelled = searchParams.get('subscription') === 'cancelled';

  const plans = useLivePlans();
  const { tiers, matrix, comparison } = livePricing(plans);

  usePageMeta({
    title: 'SnowTrack pricing: flat monthly plans for snow removal contractors',
    description: `${plans.map((plan) => `${plan.name} $${formatDollars(plan.price)}`).join(', ')} per month. No per-storm fees, no card to start, and your first storm is free.`,
    path: '/pricing',
  });

  const handleCta = async (tier) => {
    if (tier.ctaType === 'sales') {
      window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('Enterprise plan enquiry')}`;
      return;
    }

    // Logged-out visitors have no company for Stripe to bill yet, so they get
    // the account first and buy from inside the app. The chosen plan rides
    // along in the query string and Register posts it as requestedPlan.
    if (!user) {
      navigate(`/register?plan=${tier.id}`);
      return;
    }

    setCheckoutError('');
    setAlreadySubscribed(false);
    setCheckoutPlan(tier.id);
    try {
      // On success this never returns - startCheckout navigates to Stripe.
      await startCheckout(tier.id);
    } catch (err) {
      console.error('Checkout failed:', err);
      setAlreadySubscribed(isAlreadySubscribed(err));
      setCheckoutError(
        billingErrorMessage(err, 'Could not start checkout. Please try again or contact support.')
      );
      setCheckoutPlan(null);
    }
  };

  return (
    <div className="pricing-page">
      {/* Header */}
      <header className="pricing-header">
        <div className="pricing-header-content">
          <a href="/" className="pricing-logo">
            <span className="logo-icon">&#10052;&#65039;</span>
            <span className="logo-text">{getAppName()}</span>
          </a>
          <nav className="pricing-nav">
            {user ? (
              <button onClick={() => navigate('/dashboard')} className="nav-btn nav-btn-primary">
                Back to Dashboard
              </button>
            ) : (
              <>
                <button onClick={() => navigate('/login')} className="nav-btn">Log In</button>
                <button onClick={() => navigate('/register')} className="nav-btn nav-btn-primary">Get Started</button>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pricing-hero">
        <h1 className="pricing-title">Simple, flat monthly pricing</h1>
        <p className="pricing-subtitle">
          One price per month. No usage charges, no per-storm fees, no card required to
          start. Your free trial covers one complete storm event, start to finish.
        </p>
      </section>

      {/* Pricing Cards */}
      <section className="pricing-cards-section">
        {checkoutCancelled && (
          <div className="pricing-notice pricing-notice-info" role="status">
            Checkout was cancelled. You have not been charged and nothing changed on your
            account. Pick a plan below whenever you are ready.
          </div>
        )}

        {checkoutError && (
          <div className="pricing-notice pricing-notice-error" role="alert">
            {checkoutError}
            {alreadySubscribed && user?.role === 'owner' && (
              <>
                {' '}
                <button
                  type="button"
                  className="nav-btn nav-btn-primary"
                  onClick={() => openBillingPortal().catch((err) =>
                    setCheckoutError(billingErrorMessage(err, 'Could not open the billing portal. Please try again.')))}
                >
                  Manage Billing
                </button>
              </>
            )}
          </div>
        )}

        <div className="pricing-cards">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              className={`pricing-card ${tier.highlighted ? 'highlighted' : ''}`}
            >
              {tier.badge && <div className="tier-badge">{tier.badge}</div>}

              <div className="card-header">
                <h2 className="tier-name">{tier.name}</h2>
                <p className="tier-description">{tier.description}</p>
              </div>

              <div className="price-container">
                <span className="price-currency">$</span>
                <span className="price-amount">{formatDollars(tier.price)}</span>
                <span className="price-period">/mo</span>
              </div>

              <p className="flat-price-note">Flat monthly rate</p>
              <p className="tier-limits">{tier.limits}</p>

              <button
                className={`cta-button ${tier.highlighted ? 'cta-highlighted' : ''} ${tier.ctaType === 'sales' ? 'cta-sales' : ''}`}
                onClick={() => handleCta(tier)}
                disabled={checkoutPlan !== null}
              >
                {/* Signed-in visitors go straight to Stripe, so "Start Free
                    Trial" would be a lie - they are already on the trial. */}
                {checkoutPlan === tier.id
                  ? 'Starting checkout...'
                  : (user && tier.ctaType !== 'sales' ? `Choose ${tier.name}` : tier.cta)}
              </button>

              <ul className="feature-list">
                {tier.features.map((feature, idx) => (
                  <li
                    key={idx}
                    className={`feature-item ${feature.included ? 'included' : 'excluded'} ${feature.isHeader ? 'feature-header' : ''}`}
                  >
                    {!feature.isHeader && (
                      <span className="feature-icon">
                        {feature.included ? '✓' : '✕'}
                      </span>
                    )}
                    <span className="feature-text">{feature.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="comparison-section">
        <h2 className="section-title">Compare All Features</h2>

        {/* Mobile Accordion View */}
        <div className="comparison-accordion-mobile">
          {comparison.map((plan, idx) => (
            <div
              key={plan.id}
              className={`comparison-accordion-item ${openComparison === idx ? 'open' : ''} ${plan.badge ? 'highlighted' : ''}`}
            >
              <button
                className="comparison-accordion-header"
                onClick={() => setOpenComparison(openComparison === idx ? null : idx)}
              >
                <div className="accordion-plan-info">
                  <span className="accordion-plan-name">{plan.name}</span>
                  {plan.badge && <span className="accordion-badge">{plan.badge}</span>}
                  <span className="accordion-plan-price">${formatDollars(plan.price)}/mo</span>
                </div>
                <span className="accordion-icon">{openComparison === idx ? '−' : '+'}</span>
              </button>
              {openComparison === idx && (
                <div className="comparison-accordion-content">
                  <ul className="accordion-feature-list">
                    {plan.features.map((feature, fIdx) => (
                      <li
                        key={fIdx}
                        className={feature.value === true ? 'included' : feature.value === false ? 'excluded' : 'neutral'}
                      >
                        <span className="accordion-feature-icon">
                          {feature.value === true ? '✓' : feature.value === false ? '✕' : '•'}
                        </span>
                        <span className="accordion-feature-name">
                          {feature.name}
                          {typeof feature.value === 'string' && (
                            <span className="feature-note"> ({feature.value})</span>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Desktop Table View */}
        <div className="comparison-table-wrapper">
          <table className="comparison-table">
            <thead>
              <tr>
                <th className="feature-col">Feature</th>
                {TIERS.map((tier) => (
                  <th
                    key={tier.id}
                    className={`tier-col ${tier.highlighted ? 'highlight-col' : ''}`}
                  >
                    {tier.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {matrix.map((group) => (
                <React.Fragment key={group.category}>
                  <tr className="category-row">
                    <td colSpan={TIER_ORDER.length + 1}>{group.category}</td>
                  </tr>
                  {group.rows.map((row) => (
                    <tr key={row.label}>
                      <td>{row.label}</td>
                      {row.values.map((value, i) => (
                        <td
                          key={TIER_ORDER[i]}
                          className={TIERS[i].highlighted ? 'highlight-col' : ''}
                        >
                          <MatrixCell value={value} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <h2 className="section-title">Frequently Asked Questions</h2>
        <div className="faq-list">
          {FAQS.map((faq, idx) => (
            <div
              key={idx}
              className={`faq-item ${openFaq === idx ? 'open' : ''}`}
            >
              <button
                className="faq-question"
                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
              >
                <span>{faq.question}</span>
                <span className="faq-icon">{openFaq === idx ? '−' : '+'}</span>
              </button>
              {openFaq === idx && (
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="final-cta">
        <h2>Ready to streamline your snow removal business?</h2>
        <p>Set up your customers and routes, then run a real storm before you pay anything.</p>
        <button
          className="cta-button cta-large"
          onClick={() => navigate('/register')}
        >
          Start Your Free Trial
        </button>
        <p className="cta-note">No credit card required &bull; 1 storm event included</p>
      </section>

      {/* Footer */}
      <footer className="pricing-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <span className="logo-icon">&#10052;&#65039;</span>
            <span>{getAppName()}</span>
          </div>
          <div className="footer-legal">
            <a href="/privacy">Privacy Policy</a>
            <a href="/terms">Terms of Service</a>
            <a href={`mailto:${SUPPORT_EMAIL}`}>Contact</a>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} {getAppName()}. All rights reserved.</p>
        </div>
      </footer>

      <style>{`
        /* Flat monthly price note (replaces the per-storm / unlimited notes) */
        .flat-price-note {
          text-align: center;
          font-size: 0.95rem;
          color: #6b7280;
          font-weight: 500;
          margin: 0 0 8px 0;
        }

        /* What the plan includes, in seats and customers. The real ladder. */
        .tier-limits {
          text-align: center;
          font-size: 0.95rem;
          color: #111827;
          font-weight: 600;
          margin: 0 0 16px 0;
        }

        /* Contact Sales button */
        .cta-button.cta-sales {
          background: white;
          color: var(--color-primary);
          border: 2px solid var(--color-primary);
        }

        .cta-button.cta-sales:hover {
          background: #f0f7ff;
        }

        .accordion-feature-list li.neutral .accordion-feature-icon {
          color: #9ca3af;
        }

        /* Footer legal links */
        .footer-legal {
          display: flex;
          gap: 24px;
          flex-wrap: wrap;
          justify-content: center;
        }

        .footer-legal a {
          color: #6b7280;
          text-decoration: none;
          font-size: 0.9rem;
        }

        .footer-legal a:hover {
          color: var(--color-primary);
          text-decoration: underline;
        }

        .pricing-page {
          min-height: 100vh;
          background: #ffffff;
        }

        /* Header */
        .pricing-header {
          background: white;
          border-bottom: 1px solid #e5e7eb;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .pricing-header-content {
          max-width: 1280px;
          margin: 0 auto;
          padding: 16px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .pricing-logo {
          display: flex;
          align-items: center;
          gap: 8px;
          text-decoration: none;
          color: inherit;
        }

        .logo-icon {
          font-size: 1.5rem;
        }

        .logo-text {
          font-size: 1.25rem;
          font-weight: 700;
          color: var(--color-primary);
        }

        .pricing-nav {
          display: flex;
          gap: 12px;
        }

        .nav-btn {
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          border: none;
          background: transparent;
          color: #374151;
        }

        .nav-btn:hover {
          background: #f3f4f6;
        }

        .nav-btn-primary {
          background: var(--color-primary);
          color: white;
        }

        .nav-btn-primary:hover {
          background: var(--color-primary-dark);
        }

        /* Hero */
        .pricing-hero {
          text-align: center;
          padding: 60px 24px 40px;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
        }

        .pricing-title {
          font-size: 2.75rem;
          font-weight: 800;
          color: #111827;
          margin-bottom: 16px;
          letter-spacing: -0.02em;
        }

        .pricing-subtitle {
          font-size: 1.25rem;
          color: #6b7280;
          max-width: 500px;
          margin: 0 auto 32px;
        }

        /* Billing Toggle */
        /* Pricing Cards */
        .pricing-cards-section {
          padding: 20px 24px 60px;
          max-width: 1280px;
          margin: 0 auto;
        }

        .pricing-cards {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          align-items: start;
        }

        /* Checkout feedback: returned-from-Stripe notice, and failures from
           POST /api/billing/checkout. */
        .pricing-notice {
          border-radius: 10px;
          padding: 14px 18px;
          margin: 0 0 20px 0;
          font-size: 0.92rem;
          line-height: 1.5;
        }

        .pricing-notice-info {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          color: #1e3a8a;
        }

        .pricing-notice-error {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #991b1b;
        }

        .pricing-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 28px 24px;
          position: relative;
          transition: all 0.3s ease;
        }

        .pricing-card:hover {
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          transform: translateY(-4px);
        }

        .pricing-card.highlighted {
          border: 2px solid var(--color-primary);
          box-shadow: 0 20px 40px rgba(30, 136, 229, 0.15);
          transform: scale(1.03);
          z-index: 1;
        }

        .pricing-card.highlighted:hover {
          transform: scale(1.03) translateY(-4px);
        }

        .tier-badge {
          position: absolute;
          top: -12px;
          left: 50%;
          transform: translateX(-50%);
          background: var(--color-primary);
          color: white;
          padding: 6px 16px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          white-space: nowrap;
        }

        .card-header {
          text-align: center;
          margin-bottom: 20px;
        }

        .tier-name {
          font-size: 1.35rem;
          font-weight: 700;
          color: #111827;
          margin-bottom: 6px;
        }

        .tier-description {
          font-size: 0.9rem;
          color: #6b7280;
        }

        .price-container {
          text-align: center;
          margin-bottom: 4px;
        }

        .price-currency {
          font-size: 1.25rem;
          font-weight: 600;
          color: #111827;
          vertical-align: top;
          position: relative;
          top: 8px;
        }

        .price-amount {
          font-size: 3rem;
          font-weight: 800;
          color: #111827;
          line-height: 1;
          letter-spacing: -0.02em;
        }

        .price-period {
          font-size: 1rem;
          color: #6b7280;
          font-weight: 500;
        }

        .cta-button {
          display: block;
          width: 100%;
          padding: 12px 20px;
          border: none;
          border-radius: 10px;
          font-size: 0.95rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          background: #f3f4f6;
          color: #374151;
          margin: 0 0 24px 0;
        }

        .cta-button:hover {
          background: #e5e7eb;
        }

        .cta-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .cta-button.cta-highlighted {
          background: var(--color-primary);
          color: white;
        }

        .cta-button.cta-highlighted:hover {
          background: var(--color-primary-dark);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(30, 136, 229, 0.4);
        }

        .feature-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .feature-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 8px 0;
          border-top: 1px solid #f3f4f6;
          font-size: 0.875rem;
        }

        .feature-item.feature-header {
          font-weight: 600;
          color: #111827;
          border-top: none;
          padding-top: 0;
        }

        .feature-item.included {
          color: #374151;
        }

        .feature-item.excluded {
          color: #9ca3af;
        }

        .feature-icon {
          flex-shrink: 0;
          width: 18px;
          height: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          font-size: 0.7rem;
          font-weight: 700;
        }

        .feature-item.included .feature-icon {
          background: #dcfce7;
          color: #166534;
        }

        .feature-item.excluded .feature-icon {
          background: #f3f4f6;
          color: #9ca3af;
        }

        /* Comparison Table */
        .comparison-section {
          padding: 60px 24px;
          max-width: 1100px;
          margin: 0 auto;
        }

        .section-title {
          text-align: center;
          font-size: 2rem;
          font-weight: 700;
          color: #111827;
          margin-bottom: 40px;
        }

        /* Mobile Accordion - Hidden by default, shown on mobile */
        .comparison-accordion-mobile {
          display: none;
        }

        .comparison-accordion-item {
          background: white;
          border-radius: 12px;
          margin-bottom: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.08);
          border: 1px solid #e5e7eb;
        }

        .comparison-accordion-item.highlighted {
          border: 2px solid var(--color-primary);
        }

        .comparison-accordion-header {
          width: 100%;
          padding: 16px 20px;
          border: none;
          background: none;
          text-align: left;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
        }

        .comparison-accordion-header:active {
          background: #f9fafb;
        }

        .accordion-plan-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .accordion-plan-name {
          font-size: 1.1rem;
          font-weight: 700;
          color: #111827;
        }

        .accordion-badge {
          display: inline-block;
          background: var(--color-primary);
          color: white;
          font-size: 0.65rem;
          padding: 2px 8px;
          border-radius: 10px;
          font-weight: 600;
          text-transform: uppercase;
          width: fit-content;
        }

        .accordion-plan-price {
          font-size: 0.95rem;
          color: #374151;
          font-weight: 600;
        }

        .accordion-icon {
          font-size: 1.5rem;
          color: #6b7280;
          font-weight: 300;
          flex-shrink: 0;
        }

        .comparison-accordion-content {
          padding: 0 20px 20px;
          border-top: 1px solid #f3f4f6;
        }

        .accordion-feature-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .accordion-feature-list li {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 0;
          border-bottom: 1px solid #f3f4f6;
          font-size: 0.9rem;
        }

        .accordion-feature-list li:last-child {
          border-bottom: none;
        }

        .accordion-feature-list li.included {
          color: #374151;
        }

        .accordion-feature-list li.excluded {
          color: #9ca3af;
        }

        .accordion-feature-icon {
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          font-size: 0.7rem;
          font-weight: 700;
          flex-shrink: 0;
        }

        .accordion-feature-list li.included .accordion-feature-icon {
          background: #dcfce7;
          color: #166534;
        }

        .accordion-feature-list li.excluded .accordion-feature-icon {
          background: #f3f4f6;
          color: #9ca3af;
        }

        .feature-note {
          color: #6b7280;
          font-size: 0.85rem;
        }

        .comparison-table-wrapper {
          overflow-x: auto;
        }

        .comparison-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.9rem;
        }

        .comparison-table th,
        .comparison-table td {
          padding: 12px 14px;
          text-align: center;
          border-bottom: 1px solid #e5e7eb;
        }

        .comparison-table th {
          font-weight: 600;
          color: #374151;
          background: #f9fafb;
        }

        .comparison-table .feature-col {
          text-align: left;
          width: 30%;
        }

        .comparison-table .tier-col {
          width: 17.5%;
        }

        .comparison-table .highlight-col {
          background: #eff6ff;
        }

        .comparison-table th.highlight-col {
          background: var(--color-primary);
          color: white;
        }

        .category-row td {
          font-weight: 700;
          color: #111827;
          background: #f9fafb;
          text-align: left !important;
          padding-top: 20px;
        }

        .check {
          color: #16a34a;
          font-weight: 700;
          font-size: 1.1rem;
        }

        .cross {
          color: #9ca3af;
          font-size: 1rem;
        }

        /* FAQ Section */
        .faq-section {
          padding: 60px 24px;
          background: #f9fafb;
        }

        .faq-list {
          max-width: 700px;
          margin: 0 auto;
        }

        .faq-item {
          background: white;
          border-radius: 12px;
          margin-bottom: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }

        .faq-question {
          width: 100%;
          padding: 20px 24px;
          border: none;
          background: none;
          text-align: left;
          font-size: 1rem;
          font-weight: 600;
          color: #111827;
          cursor: pointer;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
        }

        .faq-question:hover {
          background: #f9fafb;
        }

        .faq-icon {
          font-size: 1.5rem;
          color: #6b7280;
          font-weight: 300;
        }

        .faq-answer {
          padding: 0 24px 20px;
        }

        .faq-answer p {
          color: #6b7280;
          line-height: 1.6;
          margin: 0;
        }

        /* Final CTA */
        .final-cta {
          text-align: center;
          padding: 80px 24px;
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
          color: white;
        }

        .final-cta h2 {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 12px;
          color: white;
        }

        .final-cta > p {
          font-size: 1.1rem;
          color: rgba(255, 255, 255, 0.95);
          margin-bottom: 32px;
        }

        .cta-large {
          display: inline-block;
          width: auto;
          padding: 16px 40px;
          font-size: 1.1rem;
          background: white;
          color: var(--color-primary);
          margin: 0;
        }

        .cta-large:hover {
          background: #f8fafc;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.2);
        }

        .cta-note {
          margin-top: 16px;
          font-size: 0.9rem;
          color: rgba(255, 255, 255, 0.9);
        }

        /* Footer */
        .pricing-footer {
          background: #111827;
          color: white;
          padding: 32px 24px;
        }

        .footer-content {
          max-width: 1280px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .footer-brand {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
        }

        .footer-copy {
          color: #9ca3af;
          font-size: 0.9rem;
        }

        /* Mobile Responsive */

        /* Tablet - 768px to 1100px */
        @media (max-width: 1100px) {
          .pricing-cards {
            grid-template-columns: repeat(2, 1fr);
            max-width: 700px;
            margin: 0 auto;
          }

          .pricing-card.highlighted {
            transform: scale(1.02);
          }

          .pricing-card.highlighted:hover {
            transform: scale(1.02) translateY(-4px);
          }
        }

        /* Tablet Portrait - 768px */
        @media (max-width: 768px) {
          .pricing-header-content {
            padding: 12px 16px;
          }

          .pricing-nav {
            gap: 8px;
          }

          .nav-btn {
            padding: 8px 12px;
            font-size: 0.9rem;
          }

          .pricing-hero {
            padding: 32px 16px 24px;
          }

          .pricing-title {
            font-size: 1.75rem;
            line-height: 1.2;
          }

          .pricing-subtitle {
            font-size: 1rem;
            margin-bottom: 24px;
          }

          .pricing-cards-section {
            padding: 20px 16px 40px;
          }

          .pricing-cards {
            grid-template-columns: 1fr;
            max-width: 100%;
            gap: 16px;
          }

          .pricing-card {
            padding: 24px 20px;
          }

          .pricing-card.highlighted {
            transform: none;
            order: -1;
            border-width: 2px;
          }

          .pricing-card.highlighted:hover {
            transform: none;
          }

          .tier-badge {
            font-size: 0.7rem;
            padding: 5px 12px;
          }

          .tier-name {
            font-size: 1.25rem;
          }

          .tier-description {
            font-size: 0.85rem;
          }

          .price-amount {
            font-size: 2.5rem;
          }

          .price-currency {
            font-size: 1.1rem;
            top: 6px;
          }

          .cta-button {
            padding: 14px 20px;
            font-size: 1rem;
            min-height: 48px;
          }

          .feature-item {
            font-size: 0.9rem;
            padding: 10px 0;
          }

          .feature-icon {
            width: 20px;
            height: 20px;
            font-size: 0.75rem;
          }

          .comparison-section {
            padding: 40px 16px;
          }

          .section-title {
            font-size: 1.5rem;
            margin-bottom: 24px;
          }

          /* Show mobile accordion, hide desktop table */
          .comparison-accordion-mobile {
            display: block;
          }

          .comparison-table-wrapper {
            display: none;
          }

          .faq-section {
            padding: 40px 16px;
          }

          .faq-item {
            margin-bottom: 10px;
          }

          .faq-question {
            padding: 16px 20px;
            font-size: 0.95rem;
            min-height: 56px;
          }

          .faq-icon {
            font-size: 1.25rem;
            flex-shrink: 0;
          }

          .faq-answer {
            padding: 0 20px 16px;
          }

          .faq-answer p {
            font-size: 0.9rem;
            line-height: 1.6;
          }

          .final-cta {
            padding: 48px 16px;
          }

          .final-cta h2 {
            font-size: 1.5rem;
            line-height: 1.3;
          }

          .final-cta > p {
            font-size: 1rem;
            margin-bottom: 24px;
          }

          .cta-large {
            width: 100%;
            max-width: 300px;
            padding: 16px 32px;
          }

          .cta-note {
            font-size: 0.85rem;
          }

          .footer-content {
            flex-direction: column;
            gap: 12px;
            text-align: center;
          }

          .footer-copy {
            font-size: 0.85rem;
          }
        }

        /* Mobile - 375px (iPhone) */
        @media (max-width: 480px) {
          .pricing-header-content {
            padding: 10px 12px;
          }

          .logo-icon {
            font-size: 1.25rem;
          }

          .logo-text {
            font-size: 1.1rem;
          }

          .nav-btn {
            padding: 8px 10px;
            font-size: 0.85rem;
          }

          .pricing-hero {
            padding: 24px 12px 20px;
          }

          .pricing-title {
            font-size: 1.5rem;
          }

          .pricing-subtitle {
            font-size: 0.95rem;
            padding: 0 8px;
          }

          .pricing-cards-section {
            padding: 16px 12px 32px;
          }

          .pricing-cards {
            gap: 12px;
          }

          .pricing-card {
            padding: 20px 16px;
          }

          .card-header {
            margin-bottom: 16px;
          }

          .tier-name {
            font-size: 1.2rem;
          }

          .price-container {
            margin-bottom: 2px;
          }

          .price-amount {
            font-size: 2.25rem;
          }

          .cta-button {
            margin-bottom: 20px;
          }

          .feature-item {
            font-size: 0.85rem;
            padding: 8px 0;
            gap: 8px;
          }

          .comparison-section {
            padding: 32px 12px;
          }

          .section-title {
            font-size: 1.25rem;
          }

          .comparison-table-wrapper {
            margin: 0 -12px;
            padding: 0 12px;
          }

          .comparison-table {
            font-size: 0.75rem;
          }

          .comparison-table th,
          .comparison-table td {
            padding: 8px 6px;
          }

          .faq-section {
            padding: 32px 12px;
          }

          .faq-question {
            padding: 14px 16px;
            font-size: 0.9rem;
            gap: 12px;
          }

          .faq-answer {
            padding: 0 16px 14px;
          }

          .faq-answer p {
            font-size: 0.85rem;
          }

          .final-cta {
            padding: 40px 12px;
          }

          .final-cta h2 {
            font-size: 1.25rem;
            padding: 0 8px;
          }

          .final-cta > p {
            font-size: 0.95rem;
          }

          .cta-large {
            width: 100%;
            max-width: 100%;
            font-size: 1rem;
          }

          .pricing-footer {
            padding: 24px 12px;
          }
        }

        /* Extra small devices */
        @media (max-width: 360px) {
          .pricing-title {
            font-size: 1.35rem;
          }

          .price-amount {
            font-size: 2rem;
          }

          .nav-btn {
            padding: 6px 8px;
            font-size: 0.8rem;
          }
        }

        /* Touch device improvements */
        @media (hover: none) and (pointer: coarse) {
          .pricing-card:hover {
            transform: none;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          }

          .pricing-card.highlighted:hover {
            transform: none;
          }

          .cta-button:hover {
            transform: none;
          }

          .cta-button.cta-highlighted:hover {
            transform: none;
            box-shadow: none;
          }

          .cta-large:hover {
            transform: none;
            box-shadow: none;
          }

          .faq-question:hover {
            background: none;
          }

          .cta-button:active {
            opacity: 0.9;
          }

          .faq-question:active {
            background: #f3f4f6;
          }
        }
      `}</style>
    </div>
  );
}

export default Pricing;
